import { AlertCircle } from "lucide-react";
import PropTypes from "prop-types";
import { cn } from "@/lib/utils";

/**
 * InputTokenCounter component displays the current token count with visual feedback
 * Uses a minimal style with a small colored dot indicator for context usage status
 */
export const InputTokenCounter = ({
  tokens = 0,
  maxTokens = 4096,
  isFocused = false,
  showError = false,
}) => {
  const percentage = (tokens / maxTokens) * 100;

  const getStatusColor = () => {
    if (percentage < 50) return "bg-zinc-400 dark:bg-zinc-500"; // Neutral - plenty of room
    if (percentage < 75) return "bg-amber-400 dark:bg-amber-500"; // Warning
    if (percentage < 90) return "bg-orange-400 dark:bg-orange-500"; // Caution
    return "bg-red-500 dark:bg-red-400"; // Critical
  };

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "flex items-center gap-1.5 px-2 py-1 rounded-lg",
          "bg-zinc-100 dark:bg-zinc-800",
          "border border-zinc-200/50 dark:border-zinc-700/50",
          "text-xs font-medium tabular-nums",
          "transition-opacity duration-200",
          isFocused ? "opacity-100" : "opacity-80"
        )}
      >
        <span className="text-zinc-600 dark:text-zinc-300">{tokens}</span>
        <span className="text-zinc-400 dark:text-zinc-500">/</span>
        <span className="text-zinc-400 dark:text-zinc-500">{maxTokens}</span>
        <div
          className={cn(
            "w-1.5 h-1.5 rounded-full transition-colors duration-200",
            getStatusColor()
          )}
        />
      </div>
      {showError && (
        <div className="flex items-center gap-1 text-red-500 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">Limit Exceeded</span>
        </div>
      )}
    </div>
  );
};

InputTokenCounter.displayName = "InputTokenCounter";

InputTokenCounter.propTypes = {
  tokens: PropTypes.number.isRequired,
  maxTokens: PropTypes.number.isRequired,
  isFocused: PropTypes.bool,
  showError: PropTypes.bool,
};

export default InputTokenCounter;
